import { useState } from "react";
import { api } from "@/App";
import { CopyKey } from "@/components/PaymentPage";
import { ManageMachines } from "@/components/ManageMachines";
import { LostKey } from "@/components/LostKey";
import { Loader2, ShieldCheck } from "lucide-react";

const AI_KEYS = [[/^KKR-/, "Recast", "#4ff2c2"], [/^KK-/, "Gen Alpha", "#e69d27"]];
export const aiKeyProduct = (k) => AI_KEYS.find(([re]) => re.test(k.trim().toUpperCase()));

const AiKeyHint = ({ hit }) => (
  <div className="text-xs border px-3 py-2" style={{ borderColor: hit[2], color: hit[2] }} data-testid="activate-ai-key-hint">
    <b>This is a {hit[1]} key</b>
    <span className="text-[#d4d8e0]">: it needs no activation. In Resolve, paste it into the <b>Licence key</b> field of the KolorKlinic {hit[1]} panel, with your fal.ai key. It's checked online when you run a job.</span>
  </div>
);

const field = "w-full bg-black border border-[#2a2f3d] px-3 py-2 font-mono text-sm uppercase outline-none focus:border-[#00e5ff]";

export default function ActivatePage() {
  const [key, setKey] = useState("");
  const [machine, setMachine] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const hit = aiKeyProduct(key);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null); setRes(null);
    try {
      const { data } = await api.post("/denoise/activate", { key: key.trim().toUpperCase(), machine_id: machine.trim().toUpperCase() });
      setRes(data);
    } catch (x) {
      const d = x.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Check the key and the Machine ID (format XXXX-XXXX-XXXX).");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#0b0c0e] text-white px-6 py-16" data-testid="activate-page">
      <form onSubmit={submit} className="max-w-xl mx-auto space-y-4 border border-[#2a2f3d] bg-[#0e1013] p-8">
        <h1 className="font-display uppercase text-3xl flex gap-2 items-center"><ShieldCheck className="text-[#00e5ff]" /> Activate Denoise, Skin, Texture or Reactor</h1>
        <p className="text-sm text-[#9ba3b4]">Activate once per machine (up to 3). After that, the plugin works fully offline.</p>
        <p className="text-xs text-[#9ba3b4] border-l-2 border-[#e69d27] pl-3" data-testid="activate-ai-note"><b className="text-[#f2f4f8]">Gen Alpha or Recast?</b> No activation code needed: paste your key (KK-… or KKR-…) straight into the Licence key field in the plugin panel. It's checked online when you run a job.</p>
        <label className="block text-xs text-[#9ba3b4]">Licence key<input className={field} value={key} onChange={(e) => setKey(e.target.value)} placeholder="KKD-…, KKS-…, KKT-… or KKF-…" data-testid="activate-key-input" /></label>
        {hit && <AiKeyHint hit={hit} />}
        <label className="block text-xs text-[#9ba3b4]">Machine ID (shown in the plugin panel in Resolve)<input className={field} value={machine} onChange={(e) => setMachine(e.target.value)} placeholder="XXXX-XXXX-XXXX" data-testid="activate-machine-input" /></label>
        <button disabled={busy || !!hit} className="w-full px-4 py-2 text-sm font-semibold bg-[#00e5ff] text-black disabled:opacity-40 flex items-center justify-center gap-2" data-testid="activate-submit-btn">
          {busy && <Loader2 className="spin" size={14} />} Get activation code
        </button>
        {err && <p className="text-xs text-[#ff453a]" data-testid="activate-error">{err}</p>}
        {res && (
          <div className="space-y-2" data-testid="activate-result">
            <p className="text-sm">Paste this into the <b>Activation code</b> field in the plugin panel ({res.machines_used} of {res.machines_max} machines used):</p>
            <CopyKey value={res.activation_code} testid="activate-code" />
          </div>
        )}
        <a href="/" className="btn-ghost inline-block px-4 py-2 text-xs" data-testid="back-home-link">Back to KolorKlinic</a>
      </form>
      <ManageMachines />
      <LostKey />
    </div>
  );
}
